// TelcoTrack — průběh objektu ve workflow (0–100 %) podle fází (+ milníků u BUDOVA/ROZVADEC).

import { hasWorkflow } from './workflow-model';
import { progressPercentByCode, getPhaseInfoByCode } from './workflow-model';
import { normalizeObjectTypeForLookup } from './phase-codes';
import {
  budovaRequiresSvarPhase,
  getRequiredMilestones,
  inferBudovaBuildingType,
  isMilestoneComplete,
  parseBudovaMilestonesFromMetadata,
  type BudovaMilestonesFlags,
  type BudovaMilestoneEngineContext,
} from './budova-milestone-engine';
import type { BudovaBuildingType } from './budova-building-type.constants';
import {
  ROZVADEC_REQUIRED_MILESTONES,
  isRozvadecMilestoneComplete,
  parseRozvadecMilestonesFromMetadata,
  type RozvadecMilestonesFlags,
} from './rozvadec-milestone-engine';

export interface ObjectPhaseProgressOptions {
  metadata?: Record<string, unknown> | null;
  budovaMilestones?: BudovaMilestonesFlags | null;
  rozvadecMilestones?: RozvadecMilestonesFlags | null;
  budovaBuildingType?: BudovaBuildingType | null;
  /** Statusy dokopu / spojkoviště — pro ODBOCENO bez flagu v metadata. */
  budovaMilestoneCtx?: BudovaMilestoneEngineContext;
  /** `pripojovany_objekt_spec.digitalni_cesta` — bez DC je Zafouknutý terminální (100 %). */
  digitalniCesta?: number | null;
}

function roundOneDecimal(n: number): number {
  return Math.round(n * 10) / 10;
}

function clampPct(n: number): number {
  return roundOneDecimal(Math.max(0, Math.min(100, n)));
}

/** Výkop: Vykopaný 70 %, Finální +30 % → 100 %, Předaný 0 %. */
const VYKOP_PROGRESS_BY_INDEX: readonly number[] = [0, 70, 100, 100];
/** Jáma: Vykopaná 70 %, Finální 100 % (bez Předané). */
const JAMA_PROGRESS_BY_INDEX: readonly number[] = [0, 70, 100];

function progressFromFixedWeights(index: number, weights: readonly number[]): number {
  if (index < 0) return 0;
  if (index >= weights.length) return 100;
  return weights[index] ?? 0;
}

/**
 * Rovnoměrné kroky: každá fáze po startu + každý povinný milník = 1 jednotka.
 * Milníky běží na „host“ fázi (`hostPhaseIndex`); po postupu dál se berou jako hotové.
 */
function equalPhaseAndMilestoneProgress(params: {
  phaseIndex: number;
  phaseCount: number;
  hostPhaseIndex: number;
  requiredMilestoneCount: number;
  completedMilestonesOnHost: number;
}): number {
  const { phaseIndex, phaseCount, hostPhaseIndex, requiredMilestoneCount, completedMilestonesOnHost } =
    params;
  const phaseSteps = Math.max(0, phaseCount - 1);
  const totalUnits = phaseSteps + requiredMilestoneCount;
  if (totalUnits <= 0) return 100;

  let completed: number;
  if (phaseIndex < hostPhaseIndex) {
    completed = phaseIndex;
  } else if (phaseIndex === hostPhaseIndex) {
    completed = hostPhaseIndex + Math.min(requiredMilestoneCount, Math.max(0, completedMilestonesOnHost));
  } else {
    // Po host fázi: milníky hotové + index aktuální fáze (každá fáze = 1 jednotka)
    completed = requiredMilestoneCount + phaseIndex;
  }
  return clampPct((completed / totalUnits) * 100);
}

function countBudovaMilestonesComplete(
  buildingType: BudovaBuildingType | null,
  ctx: BudovaMilestoneEngineContext,
): number {
  if (!buildingType) return 0;
  return getRequiredMilestones(buildingType).filter((id) => isMilestoneComplete(buildingType, id, ctx))
    .length;
}

function countRozvadecMilestonesComplete(flags: RozvadecMilestonesFlags | null | undefined): number {
  return ROZVADEC_REQUIRED_MILESTONES.filter((id) => isRozvadecMilestoneComplete(id, flags)).length;
}

/**
 * Průběh jednoho objektu (0–100) podle aktuální fáze (+ milníků u BUDOVA / ROZVADEC).
 *
 * - **VYKOP / JAMA:** Vykopaný(á) 70 %, Finální 100 %, Předaný beze změny.
 * - **BUDOVA / ROZVADEC:** rovnoměrně fáze i milníky (stejná váha).
 * - **ostatní:** lineární podle pozice fáze ve workflow.
 */
export function getObjectPhaseProgressPercent(
  objectType: string,
  phaseCode: string | null | undefined,
  options?: ObjectPhaseProgressOptions,
): number {
  const type = normalizeObjectTypeForLookup((objectType ?? '').trim());
  const code = (phaseCode ?? '').trim();
  if (!type || !hasWorkflow(type)) return 0;
  if (!code) return 0;
  const info = getPhaseInfoByCode(code);
  if (!info) return 0;

  const index = info.phase.index;
  const phaseCount = info.workflow.phases.length;

  if (type === 'VYKOP') {
    return progressFromFixedWeights(index, VYKOP_PROGRESS_BY_INDEX);
  }
  if (type === 'JAMA') {
    return progressFromFixedWeights(index, JAMA_PROGRESS_BY_INDEX);
  }

  if (type === 'ROZVADEC') {
    const flags =
      options?.rozvadecMilestones ??
      (options?.metadata ? parseRozvadecMilestonesFromMetadata(options.metadata) : null);
    // Linear Svařený — no parallel milestone units.
    return equalPhaseAndMilestoneProgress({
      phaseIndex: index,
      phaseCount,
      hostPhaseIndex: 2,
      requiredMilestoneCount: 0,
      completedMilestonesOnHost: countRozvadecMilestonesComplete(flags),
    });
  }

  if (type === 'BUDOVA') {
    const buildingType =
      options?.budovaBuildingType ??
      inferBudovaBuildingType({
        metadata: options?.metadata ?? null,
        status: code,
      });
    const flags =
      options?.budovaMilestones ??
      (options?.metadata ? parseBudovaMilestonesFromMetadata(options.metadata) : null);
    const ctx: BudovaMilestoneEngineContext = {
      ...(options?.budovaMilestoneCtx ?? {}),
      milestones: flags ?? options?.budovaMilestoneCtx?.milestones,
    };
    const required = getRequiredMilestones(buildingType);
    // Explicit DC only: undefined = unknown → keep full workflow (incl. Svařený).
    const digitalniCesta =
      options && 'digitalniCesta' in options
        ? options.digitalniCesta
        : options?.budovaMilestoneCtx && 'digitalniCesta' in options.budovaMilestoneCtx
          ? options.budovaMilestoneCtx.digitalniCesta
          : undefined;
    // Připojená bez digitální cesty: Svařený vypadne z jmenovatele → Zafouknutý = 100 %.
    let effectivePhaseCount = phaseCount;
    let effectiveIndex = index;
    if (
      digitalniCesta !== undefined
      && code.startsWith('BUDOVA.2.')
      && !budovaRequiresSvarPhase(digitalniCesta)
      && phaseCount > 1
    ) {
      effectivePhaseCount = phaseCount - 1;
      effectiveIndex = Math.min(index, effectivePhaseCount - 1);
    }
    // Paralelní milníky na Nepřipojený (PHASE.0); po postupu dál = milníky hotové.
    return equalPhaseAndMilestoneProgress({
      phaseIndex: effectiveIndex,
      phaseCount: effectivePhaseCount,
      hostPhaseIndex: 0,
      requiredMilestoneCount: required.length,
      completedMilestonesOnHost: countBudovaMilestonesComplete(buildingType, ctx),
    });
  }

  return progressPercentByCode(code);
}

/** Průměr průběhu objektů v polygonu (prázdný seznam → 0). */
export function averageObjectPhaseProgressPercent(
  objects: ReadonlyArray<{
    type: string;
    status?: string | null;
    metadata?: Record<string, unknown> | null;
    digitalniCesta?: number | null;
  }>,
): number {
  const workflowObjects = objects.filter((o) => hasWorkflow((o.type ?? '').trim()));
  if (!workflowObjects.length) return 0;
  let sum = 0;
  for (const o of workflowObjects) {
    sum += getObjectPhaseProgressPercent(o.type, o.status ?? null, {
      metadata: o.metadata ?? null,
      ...(o.digitalniCesta !== undefined ? { digitalniCesta: o.digitalniCesta } : {}),
    });
  }
  return roundOneDecimal(sum / workflowObjects.length);
}
